/**
 * supabaseInvitations.ts — CRUD-Schicht für mandant_invitations + case_mandant_access.
 *
 * Workflow (siehe Migrations 0014 + 0015):
 *   1. Berater im Cockpit klickt "Mandant einladen →"
 *      → UI ruft createInvitation({ caseId, mandantEmail }).
 *      → INSERT in mandant_invitations; Token wird serverseitig per gen_random_uuid()
 *        erzeugt (UUID v4, 122 Bit Entropie).
 *   2. UI ruft sendMandantInvitationEmail(email, token).
 *      → ruft Supabase.auth.signInWithOtp({ email, options.emailRedirectTo: …?token=… }).
 *      → Mandant erhält Magic-Link; nach Klick landet er auf /mandant/redeem?token=…,
 *        ist über den OTP-Flow eingeloggt.
 *   3. /mandant/redeem ruft redeemInvitation(token).
 *      → RPC public.redeem_mandant_invitation(p_token) (SECURITY DEFINER):
 *        validiert Token (TTL 7 Tage, single-use, nicht widerrufen),
 *        legt case_mandant_access an, markiert Einladung als redeemed,
 *        gibt case_id zurück.
 *   4. UI navigiert auf /mandant/case/{caseId} (vereinfachter Wizard).
 *
 * Berater-Operationen:
 *   - listInvitationsForCase(caseId)   → Status-Anzeige im Cockpit
 *   - revokeInvitation(invitationId)   → vor Einlösung Token entwerten
 *
 * Sicherheits-Notizen:
 *   - Token ist nie über die SELECT-RLS für Dritte sichtbar (RLS: nur Inviter
 *     + Admin sehen die Zeile). Mandant erfährt den Token NUR aus der Magic-
 *     Link-URL — das ist by design; der Mandant braucht ihn nur ein einziges
 *     Mal beim Redeem.
 *   - revokeInvitation kann ein bereits eingelöster Datensatz NICHT „rückgängig"
 *     machen — die Einlösung ist append-only. revoked_at auf eine schon
 *     eingelöste Einladung ist semantisch ein No-op (case_mandant_access bleibt
 *     bestehen). Falls Berater den Mandanten-Zugang stoppen will, muss er
 *     case_mandant_access.revoked_at setzen (separate Funktion, kommt in C3 UI).
 */

import { getSupabaseClient } from './supabase';
import type { CaseMandantAccessRow, Database, MandantInvitationRow } from './supabaseTypes';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Bewusst sehr permissiv — Server-seitige Validierung in citext (nicht-leer);
// die echte Email-Verifikation läuft über den Magic-Link-Klick selbst.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type MandantInvitation = MandantInvitationRow;
export type CaseMandantAccess = CaseMandantAccessRow;

export interface CreateInvitationInput {
  readonly caseId: string;
  readonly mandantEmail: string;
}

/**
 * Legt eine neue Einladung an. Berater muss Owner des Case sein (RLS).
 * Pro Case ist nur EINE aktive Einladung erlaubt — wenn schon eine läuft,
 * wirft die DB einen unique_violation-Fehler (siehe Trigger 0014).
 *
 * @throws Error wenn caseId kein UUID, Email syntaktisch ungültig, oder DB-Reject.
 */
export async function createInvitation(input: CreateInvitationInput): Promise<MandantInvitation> {
  if (!UUID_RE.test(input.caseId)) {
    throw new Error('createInvitation: caseId ist kein UUID-Format');
  }
  const email = input.mandantEmail.trim().toLowerCase();
  if (!EMAIL_RE.test(email)) {
    throw new Error('createInvitation: mandantEmail ist syntaktisch ungültig');
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`createInvitation: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('createInvitation: kein authentifizierter User');
  }

  const insertPayload: Database['public']['Tables']['mandant_invitations']['Insert'] = {
    case_id: input.caseId,
    invited_by: uid,
    mandant_email: email,
  };

  // Cast `as never`: gleicher supabase-js-v2-Type-Quirk wie in
  // supabaseCaseReviews.ts und supabaseStatementVersions.ts. RLS+Trigger
  // garantieren Datenintegrität zur Laufzeit.
  const { data, error } = await sb
    .from('mandant_invitations')
    .insert(insertPayload as never)
    .select('*')
    .single();

  if (error) {
    throw new Error(`createInvitation [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Listet alle Einladungen (aktiv + eingelöst + widerrufen + abgelaufen) eines
 * Case. RLS filtert auf invited_by = auth.uid() (oder Admin).
 */
export async function listInvitationsForCase(caseId: string): Promise<MandantInvitation[]> {
  if (!UUID_RE.test(caseId)) return [];

  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('mandant_invitations')
    .select('*')
    .eq('case_id', caseId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`listInvitationsForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Widerruft eine Einladung — setzt revoked_at = now(). Eine bereits eingelöste
 * Einladung kann nicht widerrufen werden (semantisches No-op auf
 * case_mandant_access; siehe Doc-Header).
 *
 * RLS erlaubt UPDATE nur für invited_by = auth.uid() oder Admin.
 * Trigger (0014) erlaubt nur Mutation auf revoked_at — kein Token-Reset etc.
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  if (!UUID_RE.test(invitationId)) {
    throw new Error('revokeInvitation: invitationId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const update: Database['public']['Tables']['mandant_invitations']['Update'] = {
    revoked_at: new Date().toISOString(),
  };
  const { error } = await sb
    .from('mandant_invitations')
    .update(update as never)
    .eq('id', invitationId)
    // Defensiv: nur widerrufen, was noch nicht widerrufen + nicht eingelöst ist
    .is('revoked_at', null)
    .is('redeemed_at', null);

  if (error) {
    throw new Error(`revokeInvitation [${error.code ?? '??'}]: ${error.message}`);
  }
}

/**
 * Löst eine Einladung ein — wird nach Magic-Link-Klick auf /mandant/redeem
 * vom eingeloggten Mandanten aufgerufen. Wrapped die SECURITY-DEFINER-RPC
 * public.redeem_mandant_invitation(p_token).
 *
 * @returns { caseId } — Case, dem der Mandant nun zugeordnet ist.
 * @throws Error wenn Token ungültig, abgelaufen, eingelöst oder widerrufen.
 */
export async function redeemInvitation(token: string): Promise<{ caseId: string }> {
  if (!UUID_RE.test(token)) {
    throw new Error('redeemInvitation: token ist kein UUID-Format');
  }
  const sb = getSupabaseClient();
  // Cast `as never`: bekannter supabase-js-v2-Type-Inferenz-Quirk bei strict
  // Database-Schema-Map. Die Function-Args (`{ p_token: string }`) sind in
  // Database['public']['Functions']['redeem_mandant_invitation']['Args']
  // korrekt typisiert; der Inferenz-Pfad GetRpcFunctionFilterBuilderByArgs
  // fällt aber bei einzelnen Args-Definitionen ohne Union auf den Default
  // `never` zurück. Der Runtime-Aufruf ist sicher (RPC validiert serverseitig).
  const { data, error } = await sb.rpc('redeem_mandant_invitation', { p_token: token } as never);
  if (error) {
    throw new Error(`redeemInvitation [${error.code ?? '??'}]: ${error.message}`);
  }
  if (typeof data !== 'string' || !UUID_RE.test(data)) {
    throw new Error(`redeemInvitation: unerwartete Antwort vom Server: ${JSON.stringify(data)}`);
  }
  return { caseId: data };
}

/**
 * Konstruiert die Redeem-URL, die im Magic-Link-Email-Body landet.
 * Wird vom Frontend genutzt, um den emailRedirectTo-Parameter für
 * Supabase signInWithOtp zu bauen.
 *
 * Reine Funktion — kein DB-Roundtrip, deterministisch. Nutzt window.location.origin
 * für Multi-Environment-Sicherheit (Pages-Preview vs. Prod-Domain).
 */
export function buildInvitationUrl(token: string): string {
  if (typeof window === 'undefined') {
    throw new Error(
      'buildInvitationUrl: nur im Browser aufrufbar (window.location nicht verfügbar)',
    );
  }
  const base = window.location.origin;
  return `${base}/mandant/redeem?token=${encodeURIComponent(token)}`;
}

/**
 * Verschickt den Magic-Link an die Mandanten-Email. Hinter dem Klick landet
 * der Mandant auf /mandant/redeem?token=… und schließt dort den Flow ab.
 *
 * Wir nutzen denselben Magic-Link-Mechanismus wie der Berater-Login
 * (signInWithOtp + PKCE), nur mit anderem emailRedirectTo. shouldCreateUser
 * = true, damit ein noch nicht registrierter Mandant beim Klick einen
 * auth.users-Eintrag bekommt; der 0008-Trigger erzeugt dann automatisch
 * den public.users-Eintrag mit role='mandant'.
 *
 * @throws Error wenn Email-Format ungültig oder Supabase Auth fehlschlägt.
 */
export async function sendMandantInvitationEmail(email: string, token: string): Promise<void> {
  const cleanedEmail = email.trim().toLowerCase();
  if (!EMAIL_RE.test(cleanedEmail)) {
    throw new Error('sendMandantInvitationEmail: email ist syntaktisch ungültig');
  }
  if (!UUID_RE.test(token)) {
    throw new Error('sendMandantInvitationEmail: token ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const { error } = await sb.auth.signInWithOtp({
    email: cleanedEmail,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: buildInvitationUrl(token),
    },
  });

  if (error) {
    throw new Error(`sendMandantInvitationEmail: ${error.message}`);
  }
}

/**
 * Listet alle aktiven Mandant-Zugänge eines Case (für Cockpit-Anzeige).
 * Berater sieht eigene Cases; RLS filtert.
 */
export async function listMandantAccessForCase(caseId: string): Promise<CaseMandantAccess[]> {
  if (!UUID_RE.test(caseId)) return [];

  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('case_mandant_access')
    .select('*')
    .eq('case_id', caseId)
    .order('granted_at', { ascending: false });

  if (error) {
    throw new Error(`listMandantAccessForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Prüft, ob der eingeloggte User einen aktiven Mandanten-Zugang zu einem
 * konkreten Case hat. Wird vom <RequireMandantAccessForCase>-Guard genutzt.
 *
 * RLS auf case_mandant_access erlaubt einem Mandanten exakt seine eigene
 * Zeile zu sehen (mandant_user_id = auth.uid()) — wir brauchen daher keinen
 * expliziten Filter auf den User. Wenn die Liste leer ist, hat der User
 * keinen aktiven Zugang.
 *
 * Defensive Verhalten: bei caseId-Format-Fehler oder Fehler vom Server
 * geben wir `false` zurück (nicht throw). Der Guard kann dann sauber
 * redirecten, anstatt einen Error-Boundary zu triggern.
 */
export async function hasMandantAccess(caseId: string): Promise<boolean> {
  if (!UUID_RE.test(caseId)) return false;

  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('case_mandant_access')
    .select('id')
    .eq('case_id', caseId)
    .is('revoked_at', null)
    .limit(1);

  if (error) {
    console.warn('hasMandantAccess: query failed, treating as no-access', {
      code: error.code,
      message: error.message,
    });
    return false;
  }
  return Array.isArray(data) && data.length > 0;
}

/**
 * Entzieht einem Mandanten den Zugang zu einem Case. Setzt
 * case_mandant_access.revoked_at = now(). Berater-only (RLS + Trigger).
 *
 * Nach revoked_at sieht der Mandant den Case nicht mehr (Mandant-Pfad in
 * cases_select_mandant filtert auf revoked_at IS NULL).
 */
export async function revokeMandantAccess(accessId: string): Promise<void> {
  if (!UUID_RE.test(accessId)) {
    throw new Error('revokeMandantAccess: accessId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const update: Database['public']['Tables']['case_mandant_access']['Update'] = {
    revoked_at: new Date().toISOString(),
  };
  const { error } = await sb
    .from('case_mandant_access')
    .update(update as never)
    .eq('id', accessId)
    .is('revoked_at', null);

  if (error) {
    throw new Error(`revokeMandantAccess [${error.code ?? '??'}]: ${error.message}`);
  }
}
